
/**
 * @file GeneralFeatureAnnotationCardContainer.js
 * @description
 *
 * @copyright veriome labs, llc.  - all rights reserved.
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.md', which is part of this source code package.
 *
 * @author don michael
 * @since Jul 12, 2022 20:56
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { useDispatch, useSelector } from 'react-redux';

import GeneralFeatureAnnotationCard from './GeneralFeatureAnnotationCard';

import { pubSub, pubSubEvents } from 'src/services/PubSub';

import { arrayExists, objectDeepCopy, objectExists, objectKeyExists } from 'src/@common/utilities';
import genericChangeHandler from 'src/@common/genericChangeHandler';
import prepareDiseases from 'src/content/DocumentAnnotator/utilities/prepareDiseases';

import {
    annotationObjStateUpdate,
    annotationDeleteRequest,
    classificationCodeSubmitRequest,
    geneSymbolSubmitRequest,
    variantObjDeleteRequest,
    variantObjNewRequest,
    variantObjStateUpdate,
    variantGDotFindRequest,
} from 'src/store/actions/documentAnnotator/documentAnnotatorDb.actions';

function GeneralFeatureAnnotationCardContainer(props) {
    const {
        annotationObj,
    } = props;

    const dispatch = useDispatch();

    // start: redux state --------------------------
    const globalState = useSelector((state) => {
        return state.global;
    });

    const { dataLoading } = globalState;

    const documentAnnotatorDbState = useSelector((state) => {
        return state.documentAnnotatorDb;
    });

    const {
        annotationGroup,
        biocurations,
    } = documentAnnotatorDbState;
    // end: redux state -----------------------------

    const [classificationOptions, setClassificationOptions] = useState([]);
    const [diseaseOptions, setDiseaseOptions] = useState([]);
    const [fieldSchema, setFieldSchema] = useState([]);
    const [formFields, setFormFields] = useState({});
    const [formIsDirty, setFormIsDirty] = useState(false);
    const [fieldDirty, setFieldDirty] = useState({});

    useEffect(() => {
        // convert fieldSchema to an associative array
        if (objectExists(annotationObj) &&
            objectExists(annotationObj.schemaFields) &&
            arrayExists(annotationObj.schemaFields)) {

            const arr = [];

            annotationObj.schemaFields.forEach((field) => {
                arr[field.fieldName] = field;
            });

            setFieldSchema(arr);
        }

    }, [annotationObj]);

    useEffect(() => {
        if (objectExists(annotationObj)) {
            setClassificationOptions(annotationObj.schemaClassifications);
        }

    }, [annotationObj]);

    useEffect(() => {
        // prepare diseaseOptions by using geneModelDiseases to set the 'approved' 
        // property of biocuratedDiseases

        if (objectExists(annotationObj)) {

            let biocuratedDiseases = [];
            if (objectExists(biocurations) &&
                objectKeyExists(biocurations, 'diseases')) {
                biocuratedDiseases = biocurations.diseases;
            }

            const tmpDiseases = prepareDiseases(biocuratedDiseases, annotationObj.geneModelDiseases);
            setDiseaseOptions(tmpDiseases);
        }

    }, [annotationObj, annotationObj.geneModelDiseases, biocurations]);

    useEffect(() => {
        // annotation fields into local state
        if (objectExists(annotationObj.annotation)) {
            const localObj = objectDeepCopy(annotationObj.annotation);
            setFormFields(localObj);
        }

    }, [annotationObj]);

    // handles changes to main fields
    const fieldUpdater = (fieldName, value) => {

        if (fieldName) {

            let currentFormFields = null;
            let tmpFormFields = null;

            switch (fieldName) {

                case 'classificationCode':
                    tmpFormFields = objectDeepCopy(formFields);
                    tmpFormFields.classificationCode = value;
                    setFormFields(tmpFormFields);

                    // dispatch classificationCode to get the dynamic fields
                    currentFormFields = objectDeepCopy(formFields);
                    currentFormFields.classificationCode = value;
                    dispatch(classificationCodeSubmitRequest(currentFormFields));

                    setTimeout(() => {
                        pubSub.publish(pubSubEvents.AnnotationClassificationCodeSubmitted, currentFormFields._id);
                    }, 2000);
                    break;

                default:
                    setFormFields({
                        ...formFields,
                        [fieldName]: value
                    });
                    break;
            }

            setFormIsDirty(true);

            setFieldDirty({
                ...fieldDirty,
                [fieldName]: true,
            });
        }
    };

    const handleBlur = () => {
        if (formIsDirty) {
            dispatch(annotationObjStateUpdate(formFields));
            setFormIsDirty(false);
        }
    };

    const handleChange = (event, fieldName) => {
        genericChangeHandler(event, fieldName, fieldUpdater);
    };

    // handles changes to dynamic fields (ie, the annotation.fields object)
    const dynamicFieldUpdater = (fieldName, value) => {

        function updateField(currentFormFields) {

            // grab the current fields object
            const currentFieldsObj = objectDeepCopy(formFields.fields);

            // update the current field in the fields obj
            const updatedFieldsObj = {
                ...currentFieldsObj,
                [fieldName]: value,
            };

            // update the formFields with the updated fields object
            const updatedFormFields = {
                ...currentFormFields,
                fields: updatedFieldsObj,
            };

            setFormFields(updatedFormFields);
        }

        if (fieldName) {

            let currentFormFields = null;

            switch (fieldName) {

                case 'geneSymbol':
                    currentFormFields = objectDeepCopy(formFields);
                    updateField(currentFormFields);

                    // dispatch to get geneModelDiseases for this geneSymbol
                    currentFormFields.fields.geneSymbol = value;
                    dispatch(geneSymbolSubmitRequest(currentFormFields));

                    setTimeout(() => {
                        pubSub.publish(pubSubEvents.AnnotationGeneSymbolSubmitted, currentFormFields._id);
                    }, 2000);
                    break;

                default:
                    currentFormFields = objectDeepCopy(formFields);
                    updateField(currentFormFields);
                    break;
            }

            setFormIsDirty(true);
            setFieldDirty({
                ...fieldDirty,
                [fieldName]: true,
            });
        }
    };

    const handleDelete = (annotationId) => {
        dispatch(annotationDeleteRequest(annotationGroup, annotationId));
    };

    const handleDynamicFieldChange = (event, fieldName) => {
        genericChangeHandler(event, fieldName, dynamicFieldUpdater);
    };

    const handleVariantObjAdd = () => {
        // saves the annotation and variantObj to the db
        dispatch(variantObjNewRequest(formFields));
    };

    const handleVariantObjBlur = (variantObj) => {
        // pushes the variantObj back into redux 
        dispatch(variantObjStateUpdate(variantObj, formFields._id));
    };

    const handleVariantObjDelete = (variantObj) => {
        const variantObjId = variantObj._id;
        dispatch(variantObjDeleteRequest(formFields, variantObjId));
    };

    const handleGDotLookup = (variantId, cDot, transcript) => {
        const annotationId = formFields._id;
        const notify = true;
        dispatch(variantGDotFindRequest(cDot, transcript, annotationId, variantId, notify));
    };

    return (
        <GeneralFeatureAnnotationCard
            biocurations={biocurations}
            classificationOptions={classificationOptions}
            dataLoading={dataLoading}
            diseaseOptions={diseaseOptions}
            formFields={formFields}
            fieldSchema={fieldSchema}
            handleBlur={handleBlur}
            handleChange={handleChange}
            handleDelete={handleDelete}
            handleDynamicFieldChange={handleDynamicFieldChange}
            handleVariantObjAdd={handleVariantObjAdd}
            handleVariantObjBlur={handleVariantObjBlur}
            handleVariantObjDelete={handleVariantObjDelete}
            handleGDotLookup={handleGDotLookup}
        />
    )
}

GeneralFeatureAnnotationCardContainer.propTypes = {
    annotationObj: PropTypes.object.isRequired,
};

export default GeneralFeatureAnnotationCardContainer;
